import React, { useState, useRef, useMemo, useEffect } from "react";
import { ContentHeader } from "@components";
import DataTable from "../components/datatable/DataTable";
import DoctorModal from "../components/modal/DoctorModal";
import { useAuth } from "../contexts/AuthContext";
import API from "../api";
import toastr from "toastr";

interface Prefix {
  id: number;
  prefix_name: string;
}

interface Doctor {
  id?: number;
  id_number: string;
  prefix: string;
  d_name: string;
  address: string;
  qualification: string;
  mobile: string;
  email: string;
  created?: string;
  modified?: string;
}

const Doctors: React.FC = () => {
  const { user } = useAuth();
  const permissions = user?.permissions || {};

  const [showModal, setShowModal] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [prefixes, setPrefixes] = useState<Prefix[]>([]);
  const tableRef = useRef<any>(null);

  // Fetch prefixes
  useEffect(() => {
    const fetchPrefixes = async () => {
      try {
        const res = await API.get("/doctors/getPrefixes");
        setPrefixes(res.data.data || []);
      } catch (err) {
        console.error("Failed to fetch prefixes", err);
      }
    };
    fetchPrefixes();
  }, []);

  const fetchDoctors = async (
    page: number,
    search: string,
    sortBy = "d_name",
    sortOrder = "desc"
  ) => {
    try {
      const res = await API.get(
        `/doctors/getDoctors?page=${page}&pageSize=25&search=${search}&sortBy=${sortBy}&sortOrder=${sortOrder}`
      );
      return { data: res.data.data, total: res.data.total };
    } catch {
      toastr.error("Failed to fetch doctors");
      return { data: [], total: 0 };
    }
  };

  const handleAdd = () => {
    setModalMode("add");
    setSelectedDoctor(null);
    setShowModal(true);
  };

  const handleEdit = (doctor: Doctor) => {
    setModalMode("edit");
    setSelectedDoctor(doctor);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this doctor?")) return;
    try {
      await API.delete(`/doctors/deleteDoctor/${id}`);
      toastr.success("Doctor deleted successfully");
      tableRef.current?.refetch();
    } catch {
      toastr.error("Failed to delete doctor");
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedDoctor(null);
  };

  const columns = useMemo(
    () => [
      { header: "ID", accessorKey: "id" },
      { header: "ID Number", accessorKey: "id_number" },
      {
        header: "Doctor Name",
        accessorKey: "d_name",
        cell: ({ row }: any) => {
          const data = row.original;
          return `${data.prefix || ""} ${data.d_name || ""}`.trim() || "-";
        },
      },
      { header: "Qualification", accessorKey: "qualification" },
      { header: "Mobile", accessorKey: "mobile" },
      { header: "Email", accessorKey: "email" },
      {
        header: "Actions",
        id: "actions",
        cell: ({ row }: any) => {
          const data: Doctor = row.original;
          return (
            <div className="d-flex justify-content-center gap-2">
              {permissions.edit_doctor && (
                <button
                  className="btn btn-light"
                  onClick={() => handleEdit(data)}
                  title="Edit"
                >
                  <i className="fa fa-edit" />
                </button>
              )}
              {permissions.delete_doctor && (
                <button
                  className="btn btn-light"
                  onClick={() => handleDelete(data.id!)}
                  title="Delete"
                >
                  <i className="fa fa-trash" />
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [permissions]
  );

  // ── If user does not have doctor view permission, show access denied ──
  if (!permissions.doctor) {
    return (
      <div>
        <ContentHeader title="Doctors" />
        <section className="content">
          <div className="container-fluid">
            <div className="card">
              <div className="card-body text-center py-5">
                <i className="fas fa-lock fa-3x text-muted mb-3"></i>
                <h4 className="text-muted">Access Denied</h4>
                <p className="text-muted">You do not have permission to view doctors.</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center">
        <ContentHeader title="Doctors" />
        {permissions.add_doctor && (
          <button className="btn btn-light px-5" onClick={handleAdd}>
            <i className="fa fa-plus" /> Create New
          </button>
        )}
      </div>

      <section className="content">
        <div className="container-fluid">
          <div className="card card-outline card-primary">
            <div className="card-body">
              <DataTable
                ref={tableRef}
                columns={columns}
                queryFn={fetchDoctors}
                queryKey={["doctors"]}
              />
            </div>
          </div>
        </div>
      </section>

      {showModal && (
        <DoctorModal
          show={showModal}
          onClose={handleModalClose}
          mode={modalMode}
          doctorData={selectedDoctor}
          onSuccess={() => {
            handleModalClose();
            tableRef.current?.refetch();
          }}
        />
      )}
    </div>
  );
};

export default Doctors;